import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "../FooterBn";
import "../styles/InstituteProfile.css";

function InstituteProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const rateRef = useRef(null);

  const [institute, setInstitute] = useState(null);
  const [instituteAverageRatings, setInstituteAverageRatings] = useState(null);
  const [userReviews, setUserReviews] = useState([]);

  const [ratings, setRatings] = useState({
    quality_of_teaching: 3,
    curriculum: 3,
    infrastructure: 3,
    campus_environment: 3,
    communication: 3,
  });

  const [comment, setComment] = useState("");
  const [anonymous, setAnonymous] = useState(false);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Emoji scale
  const emojis = ["💩", "😡", "😩", "😐", "😄"];
  const emojiLabels = ["Terrible", "Awful", "Poor", "Mediocre", "Excellent"];

  const categories = useMemo(
    () => [
      {
        label: "Quality of Teaching",
        key: "quality_of_teaching",
        avgKey: "avg_quality_of_teaching",
        icon: "📚",
      },
      {
        label: "Curriculum & Course Content",
        key: "curriculum",
        avgKey: "avg_curriculum",
        icon: "📖",
      },
      {
        label: "Infrastructure & Facilities",
        key: "infrastructure",
        avgKey: "avg_infrastructure",
        icon: "🏫",
      },
      {
        label: "Campus Culture",
        key: "campus_environment",
        avgKey: "avg_campus_environment",
        icon: "🌿",
      },
      {
        label: "Support Services",
        key: "communication",
        avgKey: "avg_communication",
        icon: "💬",
      },
    ],
    [],
  );

  const scrollToRate = () => {
    if (rateRef.current) {
      rateRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleShowAllTeachers = () => {
    navigate(`/institutes/${id}/teachers`);
  };

  const getToken = () => localStorage.getItem("token");
  const isLoggedIn = useMemo(() => !!getToken(), []);

  const getRatingColor = (value) => {
    const v = Number(value) || 0;
    if (v <= 1) return "rgb(255,140,90)";
    if (v <= 2) return "rgb(255,178,52)";
    if (v <= 3) return "rgb(255,217,52)";
    if (v <= 4) return "rgb(109, 162, 84)";
    return "rgb(46, 187, 91)";
  };

  const fetchInstituteAndAverages = async () => {
    const results = await Promise.allSettled([
      axios.get(`https://server.goatteachers.com/api/institutes/${id}`),
      axios.get(
        `https://server.goatteachers.com/api/institutes/${id}/average-rating`,
      ),
    ]);

    const instituteRes = results[0];
    const avgRes = results[1];

    if (instituteRes.status === "rejected") {
      // Institute is required. If this fails, you can’t render the page.
      throw instituteRes.reason;
    }

    setInstitute(instituteRes.value.data);

    // Average rating is optional. If it fails (no reviews yet), just show empty state.
    if (avgRes.status === "fulfilled") {
      setInstituteAverageRatings(avgRes.value?.data?.averageRatings || null);
    } else {
      setInstituteAverageRatings(null);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(
        `https://server.goatteachers.com/api/institutes/${id}/reviews`,
      );
      setUserReviews(res?.data?.reviews || []);
    } catch (e) {
      // If backend sends 404 when no reviews, treat it as "no reviews"
      if (e?.response?.status === 404) {
        setUserReviews([]);
        return;
      }
      throw e; // real error
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        await fetchInstituteAndAverages();
        await fetchReviews();
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setError("Failed to load institute info. Please try again.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    if (isLoggedIn) return;

    // Show once shortly after page load
    const initialTimer = setTimeout(() => {
      toast.info("Please login or sign up to submit a rating.", {
        toastId: "login-required-initial",
        position: "top-right",
        autoClose: 3000,
      });
    }, 1200);

    // Show repeatedly every 15 seconds while user stays on page
    const interval = setInterval(() => {
      toast.info("Login / Sign Up required to rate this institute.", {
        toastId: `login-required-${Date.now()}`,
        position: "top-right",
        autoClose: 3000,
      });
    }, 15000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [isLoggedIn]);

  const overallRating = useMemo(() => {
    if (!instituteAverageRatings) return null;

    const nums = categories
      .map((c) => parseFloat(instituteAverageRatings[c.avgKey]))
      .filter((n) => !Number.isNaN(n));

    if (!nums.length) return null;

    const sum = nums.reduce((a, b) => a + b, 0);
    return (sum / nums.length).toFixed(1);
  }, [instituteAverageRatings, categories]);

  const handleSliderChange = (factor, value) => {
    setRatings((prev) => ({ ...prev, [factor]: value }));
  };

  const generatedRating = useMemo(() => {
    const values = Object.values(ratings);
    const sum = values.reduce((a, b) => a + b, 0);
    return (sum / values.length).toFixed(1);
  }, [ratings]);

  const submitReview = async () => {
    const token = getToken();

    if (!token) {
      toast.warning("Please login or sign up first to submit a review.", {
        position: "top-right",
        autoClose: 2500,
      });

      setTimeout(() => {
        navigate("/signup");
        setTimeout(() => window.scrollTo(0, 0), 100);
      });

      return;
    }

    if (!comment.trim()) {
      toast.error("Please write a short review.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    const reviewData = {
      institute_id: id,
      rating: ratings,
      comment,
      is_anonymous: anonymous,
    };

    try {
      setSubmitting(true);

      await axios.post(
        `https://server.goatteachers.com/api/institutes/${id}/reviews`,
        reviewData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success("Review submitted!", {
        position: "top-right",
        autoClose: 2500,
      });

      setComment("");
      await fetchInstituteAndAverages();
      await fetchReviews();
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.message ||
        "Failed to submit review. Please try again.";

      toast.error(msg, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeDislike = async (reviewId, action) => {
    const token = getToken();
    if (!token) {
      alert("You must be logged in to do this.");
      return;
    }

    try {
      await axios.post(
        `https://server.goatteachers.com/api/institutes/${id}/reviews/${reviewId}/like`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchReviews();
    } catch (e) {
      if (e?.response?.status === 400) alert(e?.response?.data?.message);
      else console.error(e);
    }
  };

  const handleFlag = async (reviewId) => {
    const token = getToken();
    if (!token) {
      alert("You must be logged in to flag a review.");
      return;
    }

    const reason = prompt("Please provide a reason for flagging the review:");
    if (!reason?.trim()) {
      alert("Flagging reason is required.");
      return;
    }

    try {
      await axios.post(
        `https://server.goatteachers.com/api/institutes/${id}/reviews/${reviewId}/flag`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert("Review flagged successfully");
      await fetchReviews();
    } catch (e) {
      console.error(e);
      alert("Failed to flag the review.");
    }
  };

  const reviewAverage = (review) => {
    const vals = [
      review.quality_of_teaching,
      review.curriculum,
      review.infrastructure,
      review.campus_environment,
      review.communication,
    ].map((n) => Number(n) || 0);

    const sum = vals.reduce((a, b) => a + b, 0);
    return (sum / vals.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="ip-page">
        <Container className="ip-shell">
          <div className="ip-skeleton ip-skeleton-hero" />
          <Row className="g-3">
            <Col lg={4}>
              <div className="ip-skeleton ip-skeleton-card" />
            </Col>
            <Col lg={8}>
              <div className="ip-skeleton ip-skeleton-card" />
            </Col>
          </Row>
          <div className="ip-skeleton ip-skeleton-big" />
        </Container>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ip-page">
        <Container className="ip-shell">
          <div className="ip-error">
            <div className="ip-error-title">Something went wrong</div>
            <div className="ip-error-sub">{error}</div>
            <Button variant="dark" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </div>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div className="ip-page">
      <ToastContainer />
      <Container className="ip-shell">
        {/* HERO */}
        <div className="ip-hero">
          <div className="ip-hero-left">
            <div className="ip-title">
              {institute?.institute_name || "Institute Name Not Available"}
            </div>

            <div className="ip-meta">
              <div className="ip-meta-row">
                <span className="ip-meta-label">Location</span>
                <span className="ip-meta-value">
                  {institute?.location || "Not Available"}
                </span>
              </div>

              <div className="ip-desc">
                {institute?.description || "Not Available"}
              </div>
            </div>
          </div>

          <div className="ip-hero-actions">
            <button
              className="ip-btn ip-btn-primary"
              onClick={handleShowAllTeachers}
            >
              Rate Faculty Members
            </button>
            <button className="ip-btn ip-btn-primary" onClick={scrollToRate}>
              Rate Institute
            </button>
          </div>
        </div>

        {/* OVERVIEW */}
        <Row className="g-3 mb-4">
          <Col lg={4}>
            <Card className="ip-card ip-card-center">
              <div className="ip-card-title">Overall Rating</div>

              {overallRating ? (
                <>
                  <div className="ip-score">
                    <span className="ip-score-value">{overallRating}</span>
                    <span className="ip-score-den">/ 5</span>
                  </div>
                  <div className="ip-muted">Based on community reviews</div>
                </>
              ) : (
                <div className="ip-empty">Be the first to review ⭐</div>
              )}
            </Card>
          </Col>

          <Col lg={8}>
            <Card className="ip-card">
              <div className="ip-card-head">
                <div className="ip-card-title">Category Ratings</div>
                <div className="ip-pill">
                  {userReviews.length} review
                  {userReviews.length === 1 ? "" : "s"}
                </div>
              </div>

              <div className="ip-breakdown">
                {categories.map((c) => {
                  const rating = instituteAverageRatings
                    ? parseFloat(instituteAverageRatings[c.avgKey])
                    : 0;
                  const width = Math.max(0, Math.min(100, (rating / 5) * 100));

                  return (
                    <div className="ip-break-row" key={c.avgKey}>
                      <div className="ip-break-left">
                        <div className="ip-break-icon">{c.icon}</div>
                        <div className="ip-break-label">{c.label}</div>
                      </div>

                      <div className="ip-break-right">
                        <div className="ip-bar">
                          <div
                            className="ip-bar-fill"
                            style={{
                              width: `${width}%`,
                              backgroundColor: getRatingColor(rating),
                            }}
                          />
                        </div>
                        <div className="ip-break-num">
                          {rating ? rating.toFixed(1) : "—"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </Col>
        </Row>

        {/* RATE SECTION */}
        <div ref={rateRef} className="ip-section">
          <div className="ip-section-title">Add Your Rating</div>
          <div className="ip-section-sub">
            Be honest. Be helpful. Be constructive.
          </div>

          <Card className="ip-card ip-rate-card">
            <Row className="g-3">
              {categories.map((c) => {
                const v = ratings[c.key];
                return (
                  <Col md={6} key={c.key}>
                    <div className="ip-factor">
                      <div className="ip-factor-top">
                        <div className="ip-factor-title">{c.label}</div>
                        <div
                          className="ip-factor-pill"
                          style={{
                            borderColor: getRatingColor(v),
                            color: getRatingColor(v),
                          }}
                        >
                          {emojiLabels[v - 1]}
                        </div>
                      </div>

                      <div className="ip-slider">
                        <div className="ip-slider-side ip-slider-nay">Nay</div>

                        <div className="ip-slider-track">
                          <input
                            className="ip-slider-input"
                            type="range"
                            min="1"
                            max="5"
                            step="1"
                            value={v}
                            onChange={(e) =>
                              handleSliderChange(
                                c.key,
                                parseInt(e.target.value),
                              )
                            }
                          />
                          <div
                            className="ip-slider-emoji"
                            style={{ left: `${(v - 1) * 25}%` }}
                          >
                            {emojis[v - 1]}
                          </div>
                        </div>

                        <div className="ip-slider-side ip-slider-yay">Yay</div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>

            <div className="ip-generated">
              <div className="ip-generated-left">
                <div className="ip-generated-title">Your Generated Rating</div>
                <div className="ip-muted">Average based on your sliders</div>
              </div>
              <div className="ip-generated-right">
                <span className="ip-generated-score">{generatedRating}</span>
                <span className="ip-generated-den">/ 5</span>
              </div>
            </div>

            <div className="ip-reviewbox">
              <div className="ip-reviewbox-title">Write a Review</div>
              <Form.Control
                as="textarea"
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="এই রিভিউ দিতে গিয়া কেউ ঝামেলায় পড়ে নাই — এখনও!"
                className="ip-textarea"
              />

              <div className="ip-actions">
                <div className="ip-toggle">
                  <label className="ip-switch">
                    <input
                      type="checkbox"
                      checked={anonymous}
                      onChange={() => setAnonymous((s) => !s)}
                    />
                    <span className="ip-switch-ui" />
                  </label>
                  <div className="ip-toggle-text">
                    {anonymous ? "Anonymity ON" : "Anonymity OFF"}
                  </div>
                </div>

                <button
                  className="ip-btn ip-btn-primary"
                  onClick={submitReview}
                  disabled={submitting && isLoggedIn}
                >
                  {!isLoggedIn
                    ? "Sign Up to Submit Review"
                    : submitting
                      ? "Submitting..."
                      : "Submit Rating"}
                </button>
              </div>
            </div>
          </Card>
        </div>

        {/* REVIEWS */}
        <div className="ip-section">
          <div className="ip-section-head">
            <div className="ip-section-title">
              User Ratings{" "}
              <span className="ip-count">({userReviews.length})</span>
            </div>
          </div>

          {userReviews.length ? (
            <Row className="g-3">
              {userReviews.map((review) => (
                <Col lg={6} key={review.institute_rating_id}>
                  <Card className="ip-card ip-review-card">
                    <div className="ip-review-head">
                      <div className="ip-review-author">
                        {review.is_anonymous
                          ? "Anonymous"
                          : review?.User?.name || "User"}
                      </div>
                      <div
                        className="ip-review-score"
                        style={{
                          borderColor: getRatingColor(reviewAverage(review)),
                        }}
                      >
                        {reviewAverage(review)} / 5
                      </div>
                    </div>

                    <div className="ip-review-text">{review.comment}</div>

                    <div className="ip-review-breakdown">
                      {categories.map((c) => {
                        const value = Number(review[c.key]) || 0;
                        const width = Math.max(
                          0,
                          Math.min(100, (value / 5) * 100),
                        );

                        return (
                          <div className="ip-mini" key={c.key}>
                            <div className="ip-mini-top">
                              <div className="ip-mini-label">{c.label}</div>
                              <div className="ip-mini-num">{value}/5</div>
                            </div>
                            <div className="ip-mini-bar">
                              <div
                                className="ip-mini-fill"
                                style={{
                                  width: `${width}%`,
                                  backgroundColor: getRatingColor(value),
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="ip-review-actions">
                      <button
                        className="ip-chip"
                        onClick={() =>
                          handleLikeDislike(review.institute_rating_id, "like")
                        }
                      >
                        👍 {review.likes}
                      </button>
                      <button
                        className="ip-chip"
                        onClick={() =>
                          handleLikeDislike(
                            review.institute_rating_id,
                            "dislike",
                          )
                        }
                      >
                        👎 {review.dislikes}
                      </button>
                      <button
                        className="ip-chip ip-chip-warn"
                        onClick={() => handleFlag(review.institute_rating_id)}
                      >
                        🚩 Flag
                      </button>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Card className="ip-card ip-empty-card">
              <div className="ip-empty">
                No reviews yet. Be the first to review ⭐
              </div>
              <button className="ip-btn ip-btn-primary" onClick={scrollToRate}>
                Write a review
              </button>
            </Card>
          )}
        </div>
      </Container>

      <Footer />
    </div>
  );
}

export default InstituteProfile;
